// src/components/MergePDFs.js

import React, { useState } from 'react';
import { PDFDocument, rgb } from 'pdf-lib';
import './Dashboard.css';
import { ReactComponent as IconMerge } from './icons/merge.svg';

const MergePDFs = () => {
  const [pdfFiles, setPdfFiles] = useState([]);

  const handleFilesUpload = (event) => {
    const files = Array.from(event.target.files);
    setPdfFiles(files);
  };

  const mergePDFs = async () => {
    if (pdfFiles.length < 2) {
      alert('Please select at least two PDF files to merge.');
      return;
    }

    const mergedPdf = await PDFDocument.create();

    for (const file of pdfFiles) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());

      copiedPages.forEach((page) => {
        // ensure page is added first
        mergedPdf.addPage(page);
        try {
          // draw a thin stroked rectangle (border) on the copied page
          const { width, height } = page.getSize();
          const borderMargin = 10; // 10 units margin
          page.drawRectangle({
            x: borderMargin / 2,
            y: borderMargin / 2,
            width: width - borderMargin,
            height: height - borderMargin,
            borderColor: rgb(0.2, 0.2, 0.2),
            borderWidth: 0.5,
            color: undefined,
          });
        } catch (e) {
          // ignore if drawRectangle isn't supported for this page
        }
      });
    }

    const mergedPdfBytes = await mergedPdf.save();

    const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'merged.pdf';
    link.click();
  };

  return (
    <div>
    <div style={styles.container}>
      <h2>🔗 Merge PDF Files</h2>
      <input
        type="file"
        accept="application/pdf"
        multiple
        onChange={handleFilesUpload}
        style={styles.input}
      />
      <br />
      <button className="tool-button" onClick={mergePDFs}><IconMerge aria-hidden="true"/> Merge & Download PDF</button>
    </div>
    <div className="content">
  <h2>Merge PDF: Bringing Multiple Documents Together Seamlessly</h2>

  <p>
    When you’re working with several PDF files—reports, invoices, contracts, scanned
    pages, or notes—keeping everything separate can be inconvenient. That’s where PDF
    merging comes in. Merging PDFs lets you combine multiple documents into a single,
    organized file that’s easier to share, store, and navigate.
  </p>

  <p>
    It’s a simple task on the surface: select your files, click “Merge,” and download
    one consolidated PDF. But behind the scenes, a structured technical process ensures
    your final file remains clean, consistent, and readable.
  </p>

  <h3>Why Merge PDFs?</h3>

  <p>Merging PDFs helps you:</p>
  <ul>
    <li>Keep related information in one place</li>
    <li>Reduce clutter by avoiding multiple separate files</li>
    <li>Create a professional, unified document</li>
    <li>Share materials more conveniently</li>
    <li>Maintain page order and structure</li>
  </ul>

  <p>
    Whether you're creating a project bundle, preparing a report, or organizing scanned
    pages, merging helps simplify your workflow.
  </p>

  <h3>How PDF Merging Works Behind the Scenes</h3>

  <h4>1. Reading Each PDF File</h4>

  <p>
    The merging engine first loads every PDF you upload. It analyzes:
  </p>

  <ul>
    <li>Page count</li>
    <li>Page sizes</li>
    <li>Embedded fonts</li>
    <li>Images and vector graphics</li>
    <li>Metadata</li>
  </ul>

  <p>Each file is treated as a collection of objects, not just pages.</p>

  <h4>2. Extracting and Normalizing Pages</h4>

  <p>Not all PDFs are created equal. Some may use Letter size, others A4. Some use embedded fonts, others reference external ones.</p>

  <p>The system ensures:</p>
  <ul>
    <li>Page sizes match or adjust safely</li>
    <li>Fonts are kept intact</li>
    <li>Images are preserved</li>
    <li>No content shifts or overlaps occur</li>
  </ul>

  <p>This normalization step ensures all pages can live together in a single document.</p>

  <h4>3. Rebuilding the Document Structure</h4>

  <p>After analyzing and normalizing, the engine creates a new PDF structure:</p>

  <ul>
    <li>A new root object</li>
    <li>A unified page tree</li>
    <li>Sequential page references</li>
    <li>A consistent resource dictionary</li>
  </ul>

  <p>
    Each original page is inserted in order, forming a continuous sequence from start
    to finish.
  </p>

  <h4>4. Writing the Final Merged PDF</h4>

  <p>
    Finally, the system compiles everything into one polished PDF file. The final output includes:
  </p>

  <ul>
    <li>All pages from all uploaded documents</li>
    <li>A corrected and unified cross-reference table</li>
    <li>A clean trailer and metadata</li>
    <li>Consistent formatting across the document</li>
  </ul>

  <p>
    The result? A reliable, portable document that opens flawlessly on any PDF reader.
  </p>
</div>
    </div>
  );
};

const styles = {
  container: {
  textAlign: 'center',
  marginTop: '50px',
  border: '1px solid #e0e0e0',
  padding: 16,
  borderRadius: 6,
  },
  input: {
  marginTop: '20px',
  padding: 6,
  border: '1px solid #ccc',
  borderRadius: 4,
  },
  button: {
    marginTop: '20px',
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
};

export default MergePDFs;
